import { useCallback, useEffect, useRef } from 'react';
import type { Strategy } from '../types/Strategy';
import { ApiService } from '../services/ApiService';
import { getAccessToken } from '../core/auth/tokenUtil';
import { serializeNode } from '../utils/serializeNode';
import type { StrategyResult, WorkspaceState } from './useWorkspaceState';
import { toast } from 'sonner';

interface UseQuantArcJobsParams {
    strategies: Strategy[];
    setWorkspaceState: (patch: Partial<WorkspaceState>) => void;
    setStrategyResult: (id: string, result: Partial<StrategyResult>) => void;
    globalStart: string | null;
    globalEnd: string | null;
    startStrategyLoading: (strategyId: string, mode: 'evaluate' | 'quantarc' | 'meepmeep') => void;
    stopStrategyLoading: (strategyId: string) => void;
}

export function useQuantArcJobs({
    strategies,
    setWorkspaceState,
    setStrategyResult,
    globalStart,
    globalEnd,
    startStrategyLoading,
    stopStrategyLoading,
}: UseQuantArcJobsParams) {
    const strategiesRef = useRef<Strategy[]>(strategies);
    const activeQuantArcJobsRef = useRef<string[]>([]);
    const activeQuantArcJobsKeyRef = useRef<string>('');
    const quantArcPollingInFlightRef = useRef<Set<string>>(new Set());
    const quantArcNotifiedJobsRef = useRef<Set<string>>(new Set());
    const quantArcAccessTokenRef = useRef<string | null>(null);

    useEffect(() => {
        strategiesRef.current = strategies;
        activeQuantArcJobsRef.current = strategies
            .filter((strategy) => strategy.loadingMode === 'quantarc' && strategy.quantArcJobId)
            .map((strategy) => strategy.quantArcJobId)
            .filter((jobId): jobId is string => Boolean(jobId));
    }, [strategies]);

    const notifyQuantArcTerminalState = useCallback((strategy: Strategy, status: string, message: string | null) => {
        const text = `QuantArc backtest ${status} for "${strategy.name}"${message ? `: ${message}` : ''}`;
        
        if (status === 'completed') {
            toast.success(text);
        } else if (status === 'failed') {
            toast.error(text);
        } else {
            toast.info(text);
        }
    }, []);

    const applyQuantArcTerminalState = useCallback((strategy: Strategy, payload: any) => {
        const status = payload.status;
        const result = payload.result;
        const error = payload.error;

        if (status === 'completed' && result) {
            const resultData = result.nature === 'insight' ? result : (result.data ?? null);
            setStrategyResult(strategy.id, {
                result: resultData,
                error: result.error ?? null,
                nature: result.nature ?? 'backtest',
                display_type: result.display_type ?? undefined,
                backendStats: result.stats ?? null,
                chartData: result.chart_data ?? null,
                chartSeriesLabels: result.chart_series_labels ?? null,
                parityReport: result.parity_report ?? null,
                isLoading: false,
                loadingMode: null,
                quantArcJobId: null,
                quantArcJobStatus: 'completed',
                quantArcJobMessage: payload.message ?? null,
            });
            setWorkspaceState({ activeTab: 'results' });
            return;
        }

        const errorMessage = error?.error || payload.message || (status === 'cancelled' ? 'QuantArc backtest cancelled.' : 'QuantArc backtest failed.');
        setStrategyResult(strategy.id, {
            isLoading: false,
            loadingMode: null,
            error: errorMessage,
            quantArcJobId: null,
            quantArcJobStatus: status,
            quantArcJobMessage: errorMessage,
        });
        setWorkspaceState({ activeTab: 'results' });
    }, [setStrategyResult, setWorkspaceState]);

    const pollQuantArcJobs = useCallback(async () => {
        const activeQuantArcStrategies = strategiesRef.current.filter(
            (strategy) => strategy.loadingMode === 'quantarc' && strategy.quantArcJobId,
        );
        if (activeQuantArcStrategies.length === 0) return;

        await Promise.all(
            activeQuantArcStrategies.map(async (strategy) => {
                const jobId = strategy.quantArcJobId;
                if (!jobId || quantArcPollingInFlightRef.current.has(jobId)) return;

                quantArcPollingInFlightRef.current.add(jobId);
                try {
                    const payload = await ApiService.getQuantArcBacktestJob(jobId);
                    setStrategyResult(strategy.id, {
                        quantArcJobStatus: payload.status ?? null,
                        quantArcJobMessage: payload.message ?? null,
                        isLoading: payload.status === 'queued' || payload.status === 'running' || payload.status === 'cancelling',
                        loadingMode: payload.status === 'queued' || payload.status === 'running' || payload.status === 'cancelling' ? 'quantarc' : null,
                    });

                    if (payload.status === 'completed' || payload.status === 'failed' || payload.status === 'cancelled') {
                        applyQuantArcTerminalState(strategy, payload);
                        if (!quantArcNotifiedJobsRef.current.has(jobId)) {
                            quantArcNotifiedJobsRef.current.add(jobId);
                            notifyQuantArcTerminalState(strategy, payload.status ?? 'unknown', payload.message ?? null);
                        }
                    }
                } catch (err: unknown) {
                    const errorMessage = err instanceof Error ? err.message : 'Failed to poll QuantArc job.';
                    setStrategyResult(strategy.id, {
                        result: null,
                        error: errorMessage,
                        nature: 'backtest',
                        backendStats: null,
                        isLoading: false,
                        loadingMode: null,
                        quantArcJobId: null,
                        quantArcJobStatus: 'failed',
                        quantArcJobMessage: errorMessage,
                    });
                } finally {
                    quantArcPollingInFlightRef.current.delete(jobId);
                }
            }),
        );
    }, [applyQuantArcTerminalState, notifyQuantArcTerminalState, setStrategyResult]);

    const runQuantArcBacktest = useCallback(async (strategy: Strategy) => {
        setWorkspaceState({ errorNode: null });
        startStrategyLoading(strategy.id, 'quantarc');

        try {
            const rootNode = strategy.nodes[0];
            if (!rootNode || rootNode.type !== 'PnL') {
                stopStrategyLoading(strategy.id);
                return;
            }

            const serialized = serializeNode(rootNode, globalStart, globalEnd);
            quantArcAccessTokenRef.current = await getAccessToken();
            const json = await ApiService.startQuantArcBacktestJob(serialized, { start_date: globalStart, end_date: globalEnd });

            if (json.success === false || !json.job_id) {
                setStrategyResult(strategy.id, {
                    result: null,
                    error: json.error?.error ?? 'QuantArc backtest failed to start.',
                    nature: 'backtest',
                    backendStats: null,
                    chartData: null,
                    chartSeriesLabels: null,
                    isLoading: false,
                    loadingMode: null,
                    quantArcJobId: null,
                    quantArcJobStatus: 'failed',
                    quantArcJobMessage: json.message ?? null,
                });
                setWorkspaceState({ activeTab: 'results' });
                return;
            }

            setStrategyResult(strategy.id, {
                result: null,
                error: null,
                nature: 'backtest',
                backendStats: null,
                chartData: null,
                chartSeriesLabels: null,
                isLoading: true,
                loadingMode: 'quantarc',
                quantArcJobId: json.job_id,
                quantArcJobStatus: json.status,
                quantArcJobMessage: json.message ?? null,
            });
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Network error';
            setStrategyResult(strategy.id, {
                result: null,
                error: errorMessage,
                nature: 'backtest',
                backendStats: null,
                isLoading: false,
                loadingMode: null,
                quantArcJobId: null,
                quantArcJobStatus: 'failed',
                quantArcJobMessage: errorMessage,
            });
            setWorkspaceState({ activeTab: 'results' });
        }
    }, [globalStart, globalEnd, setStrategyResult, setWorkspaceState, startStrategyLoading, stopStrategyLoading]);

    const cancelQuantArcBacktest = useCallback(async (strategy: Strategy) => {
        const jobId = strategy.quantArcJobId;
        if (!jobId) return;

        setStrategyResult(strategy.id, {
            isLoading: true,
            loadingMode: 'quantarc',
            quantArcJobStatus: 'cancelling',
            quantArcJobMessage: 'Cancelling QuantArc backtest...',
        });

        try {
            const json = await ApiService.cancelQuantArcBacktestJob(jobId);
            if (json.status === 'cancelled') {
                setStrategyResult(strategy.id, {
                    result: null,
                    error: 'QuantArc backtest cancelled.',
                    nature: 'backtest',
                    backendStats: null,
                    isLoading: false,
                    loadingMode: null,
                    quantArcJobId: null,
                    quantArcJobStatus: 'cancelled',
                    quantArcJobMessage: json.message ?? 'QuantArc backtest cancelled.',
                });
            } else {
                setStrategyResult(strategy.id, {
                    quantArcJobStatus: json.status,
                    quantArcJobMessage: json.message ?? 'Cancelling QuantArc backtest...',
                });
            }
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to cancel QuantArc backtest.';
            setStrategyResult(strategy.id, {
                isLoading: false,
                loadingMode: null,
                error: errorMessage,
                quantArcJobId: null,
                quantArcJobStatus: 'failed',
                quantArcJobMessage: errorMessage,
            });
        }
    }, [setStrategyResult]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            const token = quantArcAccessTokenRef.current;
            if (!token) return;
            for (const jobId of activeQuantArcJobsRef.current) {
                ApiService.cancelQuantArcBacktestJobKeepalive(jobId, token);
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    return {
        runQuantArcBacktest,
        cancelQuantArcBacktest,
        pollQuantArcJobs,
        activeQuantArcJobsKeyRef,
    };
}
